import React from "react";
import { type PropsWithChildren } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { T } from "@repo/transifex";
import { useModal } from "~/components/hooks/use-modal";
import { collectActions } from "~/providers/store/collect/actions";
import {
  useCollectFlow,
  useOrderLinesToCollect
} from "~/providers/store/collect/hooks";
import { employeeActions } from "~/providers/store/employee";
import { basketActions } from "~/providers/store/basket";
import { ModalConfirm } from "~/components/modal/modal-confirm";
import { Button } from "~/components";

type CollectLeaveGuardProps = {
  variant?: "home" | "cancel-basket";
};

export const CollectLeaveGuard = ({
  children,
  variant = "home"
}: PropsWithChildren<CollectLeaveGuardProps>) => {
  const navigate = useNavigate();
  const orderLinesToCollect = useOrderLinesToCollect();
  const collectFlow = useCollectFlow();
  const { showModal, hideModal } = useModal();

  const onConfirm = (
    originalClickHandler?: (e: React.MouseEvent) => void,
    originalEvent?: React.MouseEvent
  ) => {
    collectActions.reset();
    employeeActions.reset();
    basketActions.reset();
    basketActions.resetDiningOption();

    if (variant === "cancel-basket") {
      if (collectFlow === "pos") {
        navigate("/pos/sell", { replace: true });
      }

      if (collectFlow === "collect" || collectFlow === "collect-pos") {
        navigate("/collect", { replace: true });
      }
    }

    hideModal();

    // Execute the original click handler after cleanup
    if (originalClickHandler && originalEvent) {
      originalClickHandler(originalEvent);
    }
  };

  // This guard should only be used if the user has selected some orderLines
  // and only if they have already entered the POS flow
  if (
    orderLinesToCollect.length > 0 &&
    (collectFlow === "pos" || collectFlow === "collect-pos")
  ) {
    const childElement = children as React.ReactElement<{
      onClick?: (e: React.MouseEvent) => void;
    }>;
    const originalOnClick = childElement.props.onClick;

    return React.cloneElement(childElement, {
      onClick: (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        showModal({
          component: (
            <ModalConfirm>
              <FontAwesomeIcon
                className="text-danger-main"
                size="5x"
                icon={faTriangleExclamation}
              />

              <ModalConfirm.Title className="text-danger-main">
                {variant === "home" ? (
                  <>
                    <T _str="Are you sure you want to leave?" />
                    <br />
                    <T _str="Your current progress will be lost." />
                  </>
                ) : (
                  <>
                    <T _str="Are you sure you want to cancel?" />
                    <br />
                    <T _str="Your current progress will be lost." />
                  </>
                )}
              </ModalConfirm.Title>

              <ModalConfirm.ActionsContainer>
                <Button
                  onClick={hideModal}
                  variant="secondary"
                  size="large"
                  className="w-full"
                >
                  <T _str="Cancel" />
                </Button>

                <Button
                  onClick={() => {
                    onConfirm(originalOnClick, e);
                  }}
                  variant="danger"
                  size="large"
                  className="w-full"
                >
                  <T _str="Continue" />
                </Button>
              </ModalConfirm.ActionsContainer>
            </ModalConfirm>
          )
        });
      }
    });
  }

  if (collectFlow === "collect") {
    const childElement = children as React.ReactElement<{
      onClick?: (e: React.MouseEvent) => void;
    }>;
    const originalOnClick = childElement.props.onClick;

    return React.cloneElement(childElement, {
      onClick: (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onConfirm(originalOnClick, e);
      }
    });
  }

  return children;
};
